/* ==========================================================================
   LSC - Ingredient of the Day: stale-page refresh
   Created 5 Oct 2026 for sections/lsc-ingredient-of-the-day.liquid.

   WHY THIS EXISTS
   The daily pick is made in Liquid, so it is already in the page HTML and
   needs no JavaScript to show. But Shopify caches storefront pages, and a
   cached page shows the pick (and date) of the day it was rendered on. This
   script compares that day with the shop's real date and, only if the page
   is from an earlier day, re-renders this one section through the Section
   Rendering API and swaps it in. On a fresh page it does nothing except one
   date comparison.

   The re-render URL carries today's date (lsc_iotd=YYYY-MM-DD), so it can
   never be answered from yesterday's cache entry. Any other query
   parameters on the page (e.g. preview_theme_id) are kept.

   DEBUGGING
   ?lsc_iotd_debug=1 logs what the script decided.
   window.LscIotd.shopToday('-0400') returns today's date for that offset.
   ========================================================================== */
(function () {
  'use strict';

  // Run once per page even if several sections include the script tag.
  if (window.LscIotd) return;

  var DEBUG = /[?&]lsc_iotd_debug=1/.test(location.search);
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[lsc-iotd]'].concat([].slice.call(arguments)));
  }

  /**
   * Today's date in the shop's time zone as YYYY-MM-DD.
   * offset is Liquid's '%z' output, e.g. "-0400" or "+0530". Liquid doesn't
   * expose the IANA zone, so on the two daylight-saving switch days this can
   * be an hour off around midnight; the worst case is a refresh an hour
   * early or late.
   */
  function shopToday(offset) {
    var m = /^([+-])(\d{2}):?(\d{2})$/.exec(offset || '');
    var minutes = m ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) : 0;
    return new Date(Date.now() + minutes * 60000).toISOString().slice(0, 10);
  }

  /** Reads the JSON config the section prints inside its root element. */
  function readConfig(root) {
    var el = root.querySelector('script[data-lsc-iotd-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Fetches a fresh copy of the section and swaps it into the page.
   * Resolves true when the section was replaced.
   */
  function refresh(root, cfg, today) {
    var url = new URL(location.href);
    url.hash = '';
    url.searchParams.set('section_id', cfg.sectionId);
    url.searchParams.set('lsc_iotd', today); // unique per day: bypasses the stale cache entry

    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        // Parse in an inert document so nothing loads until it is in the page.
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-lsc-iotd]');
        if (!fresh) {
          log('no section in the response (nothing eligible today?)');
          return false;
        }
        var freshCfg = readConfig(fresh);
        // Guard: only swap if the server really produced a newer day, so a
        // clock disagreement can never cause a loop or a pointless swap.
        if (!freshCfg || freshCfg.day <= cfg.day) {
          log('server still on', freshCfg && freshCfg.day, '- keeping the page as it is');
          return false;
        }
        root.replaceWith(document.importNode(fresh, true));
        log('refreshed', cfg.day, '->', freshCfg.day, fresh.getAttribute('data-handle'));
        return true;
      })
      .catch(function (err) {
        // Fail safe: the visitor keeps yesterday's (still valid) article.
        log('refresh failed', err);
        return false;
      });
  }

  /** Checks one section root and refreshes it if its day is stale. */
  function check(root) {
    if (root.__lscIotdChecked) return;
    root.__lscIotdChecked = true;

    var cfg = readConfig(root);
    if (!cfg || cfg.mode !== 'daily' || cfg.designMode) return; // pinned / editor: never stale

    var today = shopToday(cfg.utcOffset);
    log('rendered for', cfg.day, 'today is', today);
    if (cfg.day >= today) return; // fresh page: nothing to do

    refresh(root, cfg, today);
  }

  function init() {
    var roots = document.querySelectorAll('[data-lsc-iotd]');
    for (var i = 0; i < roots.length; i++) check(roots[i]);
  }

  window.LscIotd = { shopToday: shopToday, check: check, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-check a section after it is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
